import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import type { SxProps, Theme } from '@mui/material/styles'

import { t } from '@lingui/core/macro'

type NoRemoteVideoProps = {
  className?: string
  sx?: SxProps<Theme>
  participantName?: string
  participantRole?: string
  statusLabel?: string
  isRemotePresent?: boolean
  /** Smaller variant for the local self-view. Identical to the full size on mobile. */
  compact?: boolean
}

function getInitials(name?: string) {
  if (!name) return ''
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

export function NoRemoteVideo({ className, sx, participantName, participantRole, statusLabel, isRemotePresent, compact }: NoRemoteVideoProps) {
  const label = statusLabel ?? (isRemotePresent ? t`Doctor's camera is off` : t`Waiting for the doctor...`)
  const initials = getInitials(participantName)

  // Sizes stay identical to the full size on mobile (xs/sm); only the desktop PIP (md) shrinks.
  const avatarSize = compact ? { xs: 128, sm: 160, md: 96 } : { xs: 128, sm: 160, md: 224 }
  const avatarFontSize = compact ? { xs: 40, sm: 48, md: 30 } : { xs: 40, sm: 48, md: 72 }
  const stackSpacing = compact ? { xs: 4, md: 1.5 } : 4
  const nameFontSize = compact ? { xs: '1.25rem', md: '0.95rem' } : undefined

  return (
    <Box
      className={className}
      sx={[
        {
          position: 'relative',
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          bgcolor: '#f1f5f9', // slate-100
          color: '#191c1e', // on-surface
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(234, 221, 255, 0.55) 0%, transparent 70%)',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Stack spacing={stackSpacing} alignItems="center" textAlign="center" px={3} sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #f8fafc 0%, rgba(98,27,203,0.05) 100%)',
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: avatarFontSize,
              fontWeight: 300,
              letterSpacing: '-0.025em',
              border: '4px solid rgba(98, 27, 203, 0.1)',
              boxShadow: '0 0 80px rgba(98, 27, 203, 0.08)',
            }}
          >
            {initials || '•••'}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: isRemotePresent ? '#10b981' : '#f59e0b',
              border: '4px solid #ffffff',
            }}
          />
        </Box>
        {participantName && (
          <Box>
            <Typography variant="h6" sx={{ color: '#191c1e', fontWeight: 600, lineHeight: 1.2, fontSize: nameFontSize }}>
              {participantName}
            </Typography>
            {participantRole && (
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                {participantRole}
              </Typography>
            )}
          </Box>
        )}
        <Chip
          icon={
            isRemotePresent ? (
              <VideocamOffIcon sx={{ fontSize: 18, color: '#d97706 !important' }} />
            ) : (
              <FiberManualRecordIcon sx={{ fontSize: 12, color: '#059669 !important' }} />
            )
          }
          label={label}
          sx={{
            bgcolor: isRemotePresent ? 'rgba(245, 158, 11, 0.1)' : 'rgba(16, 185, 129, 0.1)',
            border: isRemotePresent ? '1px solid rgba(245, 158, 11, 0.2)' : '1px solid rgba(16, 185, 129, 0.2)',
            color: isRemotePresent ? '#78350f' : '#064e3b',
            backdropFilter: 'blur(12px)',
            fontWeight: 600,
            px: 2,
            py: 2.5,
            '& .MuiChip-icon': { ml: 1 },
          }}
        />
      </Stack>
    </Box>
  )
}
